/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { FunctionComponent, useState } from 'react';
import {
  Box,
  CustomModalLayout,
  IconButton,
  Modal,
  RadioGroup,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import ChangeOrderSmall from 'wix-ui-icons-common/ChangeOrderSmall';
import css from './index.module.scss';
import {
  ALL_RESOLUTIONS,
  RoadmapFeatureResolution,
  RoadmapFilterType,
} from '../../../types';
import { ArticleResolution, ArticleSortType } from '@wix/answers-api';
import { allFilterOptions, ALL_SORT_OPTION } from '..';
import { DATA_HOOKS } from '../../../dataHooks';
import { COLORS } from '../../../utils/theme';
import { BI } from '../../../constants';
import { helpCenterPageClick } from '@wix/bi-logger-customer-care-data/v2';
import { useBI } from '../../../hooks/useBI';

export type RoadmapMobileSortAndFilterMenuProps = {
  articleResolution: RoadmapFeatureResolution;
  filterType: RoadmapFilterType;
  sortType: ArticleSortType;
  applyValue: (
    filter: RoadmapFilterType,
    sort: ArticleSortType,
    resolution?: RoadmapFeatureResolution
  ) => void;
};

const RoadmapMobileSortAndFilterMenu: FunctionComponent<
  RoadmapMobileSortAndFilterMenuProps
> = ({
  applyValue,
  articleResolution,
  filterType,
  sortType,
}: RoadmapMobileSortAndFilterMenuProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedResolution, setSelectedResolution] =
    useState<RoadmapFeatureResolution>(articleResolution);
  const [selectedFilter, setSelectedFilter] = useState(filterType);
  const [selectedSortType, setSelectedSortType] = useState(sortType);
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();

  const resolutionMenu = (
    <Box
      direction="vertical"
      className={css.resolutionMenu}
      paddingBottom={`30px`}
      dataHook={DATA_HOOKS.ROADMAP_MOBILE_STATUS_SELECTOR}
    >
      <Text className={css.resolutionHeader}>
        {t('roadmap.resolution-header')}
      </Text>
      <RadioGroup
        value={selectedResolution}
        onChange={(val) => setSelectedResolution(val as ArticleResolution)}
      >
        {['all', ...ALL_RESOLUTIONS].map((res) => (
          <RadioGroup.Radio
            className={css.sortAndFilterOption}
            key={res}
            value={res}
          >
            {t(`roadmap.article-list.resolutions.${res}`)}
          </RadioGroup.Radio>
        ))}
      </RadioGroup>
    </Box>
  );

  const FilterMenu = (
    <Box
      dataHook={DATA_HOOKS.ROADMAP_MOBILE_FILTER_SELECTOR}
      direction="vertical"
      className={css.filterMenu}
      paddingBottom={'30px'}
    >
      <Text size="medium" className={css.filterHeader}>
        {t('roadmap.filter-option.prefix')}
      </Text>
      <RadioGroup
        className={css.sortAndFilterOption}
        value={selectedFilter}
        onChange={(val) => setSelectedFilter(val as RoadmapFilterType)}
      >
        {allFilterOptions.map((filter) => (
          <RadioGroup.Radio key={filter} value={filter}>
            {t(`roadmap.filter-option.${filter}`)}
          </RadioGroup.Radio>
        ))}
      </RadioGroup>
    </Box>
  );

  const SortMenu = (
    <Box
      dataHook={DATA_HOOKS.ROADMAP_MOBILE_SORT_SELECTOR}
      direction="vertical"
      className={css.sortMenu}
      paddingBottom={'28px'}
    >
      <Text
        size="medium"
        className={css.sortHeader}
        style={{ paddingBottom: '12px' }}
      >
        {t('roadmap.sort-option.selected')}
      </Text>
      <RadioGroup
        value={selectedSortType}
        onChange={(val) => setSelectedSortType(val as ArticleSortType)}
      >
        {ALL_SORT_OPTION.map((sort) => (
          <RadioGroup.Radio
            className={css.sortAndFilterOption}
            key={sort}
            value={sort}
          >
            {t(`roadmap.sort-option.${sort}`)}
          </RadioGroup.Radio>
        ))}
      </RadioGroup>
    </Box>
  );

  const openModal = async () => {
    setIsModalOpen(true);
    await sendBIEvent(
      helpCenterPageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'open_sort_filter_option',
      })
    );
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    await sendBIEvent(
      helpCenterPageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'close_sort_filter_option',
      })
    );
  };

  const apply = () => {
    applyValue(selectedFilter, selectedSortType, selectedResolution);
    setIsModalOpen(false);
  };

  const modal = (
    <CustomModalLayout
      dataHook={DATA_HOOKS.FILTER_AND_SORT_MODAL_BODY}
      className={css.filterAndSortModalBody}
      primaryButtonText={t('roadmap.mobile.sort-and-filter.apply')}
      primaryButtonOnClick={apply}
      onCloseButtonClick={closeModal}
      primaryButtonProps={{ size: 'medium' }}
      title={
        <Text size="medium" className={css.sortAndFilterModalHeader}>
          {t('roadmap.mobile.sort-filter.side-panel-header')}
        </Text>
      }
      height={'756px'}
      showHeaderDivider={false}
      showFooterDivider={false}
      content={
        <Box direction="vertical">
          {resolutionMenu}
          {FilterMenu}
          {SortMenu}
        </Box>
      }
    />
  );
  return (
    <Box
      dataHook={DATA_HOOKS.MOBILE_SORT_AND_FILTER_MENU}
      className={css.mobileSortAndFilterMenu}
    >
      <IconButton
        dataHook={DATA_HOOKS.MOBILE_SORT_ICON_BUTTON}
        onClick={openModal}
        className={css.modalIconButton}
        priority="secondary"
        size='tiny'
      >
        <ChangeOrderSmall height={'11px'} width={'11px'} color={COLORS.B10} />
      </IconButton>

      <Modal
        dataHook={DATA_HOOKS.FILTER_AND_SORT_MODAL}
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        screen="mobile"
        zIndex={99999}
      >
        {modal}
      </Modal>
    </Box>
  );
};

export default RoadmapMobileSortAndFilterMenu;
